import { Box, Divider } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  ITemplate,
  ITemplatesQuery,
  TemplatesDocument,
  useCreateTemplateMutation,
  useUpdateTemplateMutation
} from '../../generated/graphql';
import { sameTemplateTitleExists } from '../../utils/clientUtils';
import { Colors } from '../../utils/theme';
import EditButton from '../Common/Buttons/EditButton';
import DialogBox from '../Common/Layout/DialogBox';
import InputComponent from '../Common/Fields/InputComponent';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';

interface IProps {
  template: ITemplate | null;
  handleClose: () => void;
}

type IErrors = {
  shortTitle: string | null;
  shortSubject: string | null;
  shortContent: string | null;
};

const ErrorTexts = {
  shortTitle: 'Title too short',
  shortSubject: 'Subject too short',
  shortContent: 'Content too short'
};

const defaultErrorsObj: IErrors = {
  shortTitle: null,
  shortSubject: null,
  shortContent: null
};

const TemplateEditor: React.FunctionComponent<IProps> = ({ template, handleClose }: IProps) => {
  const { dispatch } = useContext(AppContext);
  const [title, setTitle] = useState(template ? template.title : '');
  const [subject, setSubject] = useState(template ? template.subject : '');
  const [htmlValue, setHtmlValue] = useState(template?.content ?? '');
  const htmlInputRows = 17;
  const isNew = template === null;
  const [editable, setEditable] = useState(false);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createTemplate] = useCreateTemplateMutation();
  const [updateTemplate] = useUpdateTemplateMutation();

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value.length > 2) {
      setFormErrors({ ...formErrors, shortTitle: null });
    }
    setTitle(value);
  };

  const handleSubjectChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value.length > 2) {
      setFormErrors({ ...formErrors, shortSubject: null });
    }
    setSubject(value);
  };

  const handleCancelPressed = () => {
    if (isNew) {
      handleClose();
    } else {
      setTitle(template?.title || '');
      setSubject(template?.subject || '');
      setHtmlValue(template?.content ?? '');
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    }
  };

  const submitTemplate = () => {
    if (isNew) {
      createTemplate({
        variables: {
          input: {
            subject,
            title,
            content: htmlValue
          }
        },
        update(cache, { data }) {
          if (data?.result) {
            const templateQuery = cache.readQuery({
              query: TemplatesDocument
            }) as ITemplatesQuery;
            if (templateQuery.result) {
              const cachedTemplate = templateQuery.result.find((t) => t?.id === data.result.id);
              if (!cachedTemplate) {
                const newData = [data.result, ...templateQuery.result];
                cache.writeQuery({
                  query: TemplatesDocument,
                  data: { result: newData }
                });
              }
            }
            handleClose();
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully created template`
              }
            });
          }
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      updateTemplate({
        variables: {
          id: parseInt((template as ITemplate).id),
          input: {
            subject,
            title,
            content: htmlValue
          }
        },
        update() {
          handleClose();
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Template updated successfully'
            }
          });
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            autoHide: 6000,
            text: e.message
          }
        });
      });
    }
  };

  const handleSave = () => {
    const errors = {
      shortTitle: null,
      shortSubject: null,
      shortContent: null
    } as IErrors;

    let hasErrors = false;
    if (title.length < 2) {
      hasErrors = true;
      errors.shortTitle = ErrorTexts.shortTitle;
    }
    if (subject.length < 2) {
      hasErrors = true;
      errors.shortSubject = ErrorTexts.shortSubject;
    }
    if (htmlValue.length < 4) {
      hasErrors = true;
      errors.shortContent = ErrorTexts.shortContent;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const titleExists = sameTemplateTitleExists(title, template?.id);
      if (titleExists) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: 'There is a template with same title',
            severity: 'warning'
          }
        });
      } else {
        submitTemplate();
      }
    }
  };

  const handleHtmlChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (value.length > 3) {
      setFormErrors({ ...formErrors, shortContent: null });
    }
    setHtmlValue(value);
  };

  return (
    <DialogBox
      id="template-editor-dialog"
      data-testid="template-editor-dialog"
      title={template ? template.title : 'New Template'}
      width={1400}
      height={1002}
      onClose={handleClose}
    >
      {template && (
        <Box sx={{ position: 'absolute', right: '24px' }}>
          <EditButton
            id="template-editor-edit-button"
            onClick={() => setEditable(true)}
            disabled={editable}
            editButtonColor={editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', mt: '46px', px: '3.93%', gap: '3.78%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '45px', width: '100%' }}>
          <InputComponent
            id="template-editor-title"
            label="Title"
            value={title}
            onChange={handleTitleChange}
            disabled={!isNew && !editable}
            errorMsg={formErrors.shortTitle}
          />
          <InputComponent
            id="template-subject"
            label="Subject"
            value={subject}
            onChange={handleSubjectChange}
            disabled={!isNew && !editable}
            errorMsg={formErrors.shortSubject}
          />
          <Divider sx={{ my: '20px' }} />
          <InputComponent
            id="template-html"
            label="HTML"
            value={htmlValue}
            onChange={handleHtmlChange}
            disabled={!isNew && !editable}
            errorMsg={formErrors.shortContent}
            multiline
            rows={htmlInputRows}
          />
        </Box>
        <Box sx={{ width: '100%' }}>
          <Editor
            apiKey={'t5l2zdfj93i0j7jhpf2arppkijlh4uu5a21w93j5i8d4ehkf'}
            disabled={!isNew && !editable}
            initialValue={template?.content ?? ''}
            value={htmlValue}
            onEditorChange={(newValue) => setHtmlValue(newValue)}
            init={{
              plugins: 'link image preview imagetools code ',
              toolbar:
                'undo redo | bold italic underline | fontselect | alignleft aligncenter alignright | image link | preview',
              visual: false
            }}
          />
        </Box>
      </Box>
      {(isNew || editable) && (
        <Box className="centralize" sx={{ gridColumn: 'span 3', mt: '40px' }}>
          <CancelButton id="cancel-template-button" onClick={handleCancelPressed} text="Cancel" />
          <SaveButton
            id="create-template-button"
            onClick={handleSave}
            text={isNew ? 'Create' : 'Save'}
          />
        </Box>
      )}
    </DialogBox>
  );
};

export default TemplateEditor;
